import { isAxiosError } from "axios";

/**
 * What an error capture records of a thrown value, flat so PostHog and
 * Sentry receive the same fields. `status` is the HTTP status of an Axios
 * failure; `error_code` is the error's `code` (`ERR_NETWORK`, `ECONNRESET`).
 */
export type NormalizedError = {
  error_name: string | null;
  error_message: string | null;
  error_code: string | null;
  status: number | null;
  error_stack: string | null;
  error_cause: string[] | null;
};

const MESSAGE_MAX_CHARS = 500;
const STACK_MAX_CHARS = 2000;
const CAUSE_MAX_DEPTH = 5;

export function normalizeError(error: unknown): NormalizedError {
  if (error === undefined) {
    return {
      error_name: null,
      error_message: null,
      error_code: null,
      status: null,
      error_stack: null,
      error_cause: null,
    };
  }

  const status = httpStatus(error);
  const code = errorCode(error);

  if (!(error instanceof Error)) {
    return {
      error_name: typeof error,
      error_message: truncate(describeValue(error), MESSAGE_MAX_CHARS),
      error_code: code,
      status,
      error_stack: null,
      error_cause: null,
    };
  }

  const causes = causeChain(error);

  return {
    error_name: error.name,
    error_message: truncate(error.message, MESSAGE_MAX_CHARS),
    error_code: code,
    status,
    error_stack: truncate(error.stack ?? null, STACK_MAX_CHARS),
    error_cause: causes.length > 0 ? causes : null,
  };
}

function httpStatus(error: unknown): number | null {
  if (!isAxiosError(error)) return null;

  return error.response?.status ?? null;
}

function errorCode(error: unknown): string | null {
  if (typeof error !== "object" || error === null) return null;

  const { code } = error as { code?: unknown };

  return typeof code === "string" ? code : null;
}

/** `Error: outer` → `["TypeError: inner", "Error: root"]`, outermost first, up to CAUSE_MAX_DEPTH. */
function causeChain(error: Error): string[] {
  const chain: string[] = [];
  let current: unknown = error.cause;

  while (current !== undefined && chain.length < CAUSE_MAX_DEPTH) {
    chain.push(truncate(describeValue(current), MESSAGE_MAX_CHARS));

    current = current instanceof Error ? current.cause : undefined;
  }

  return chain;
}

function describeValue(value: unknown): string {
  if (value instanceof Error) return `${value.name}: ${value.message}`;

  if (typeof value === "string") return value;

  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return stringOrPlaceholder(value);
  }
}

/** `String(value)` can itself throw (a `toString` that throws); the event must still go out. */
function stringOrPlaceholder(value: unknown): string {
  try {
    return String(value);
  } catch {
    return `[unserializable ${typeof value}]`;
  }
}

function truncate(text: string, max: number): string;
function truncate(text: string | null, max: number): string | null;
function truncate(text: string | null, max: number): string | null {
  if (text === null || text.length <= max) return text;

  return `${text.slice(0, max)}…`;
}
